import React from 'react';

export interface HorarioSlot {
  tempId: string;
  tipo_slot: 'grupal' | 'privada' | 'operacion';
  dia_semana: string;
  hora_inicio: string;
  hora_fin: string;
  capacidad_maxima: number | null;
  modalidad: 'presencial' | 'virtual' | 'hibrido';
  ubicacion: string;
  requiere_staff?: boolean;
}

interface HorariosBuilderProps {
  tipoSlot: 'grupal' | 'privada' | 'operacion';
  horarios: HorarioSlot[];
  showCapacity?: boolean;
  onChange: (slots: HorarioSlot[]) => void;
}

const DIAS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

function newTempId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `h-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}

export function HorariosBuilder({ tipoSlot, horarios, showCapacity = true, onChange }: HorariosBuilderProps) {
  const addSlot = () => {
    onChange([
      ...horarios,
      {
        tempId: newTempId(),
        tipo_slot: tipoSlot,
        dia_semana: 'lunes',
        hora_inicio: '09:00',
        hora_fin: '10:00',
        capacidad_maxima: showCapacity ? 10 : null,
        modalidad: 'presencial',
        ubicacion: '',
        requiere_staff: false,
      },
    ]);
  };

  const updateSlot = (tempId: string, changes: Partial<HorarioSlot>) => {
    onChange(horarios.map((h) => (h.tempId === tempId ? { ...h, ...changes } : h)));
  };

  const removeSlot = (tempId: string) => {
    onChange(horarios.filter((h) => h.tempId !== tempId));
  };

  const showStaffToggle = tipoSlot === 'grupal' || tipoSlot === 'privada';
  const gridCols = showCapacity
    ? showStaffToggle
      ? '120px 80px 80px 70px 110px minmax(88px,auto) 1fr 24px'
      : '120px 80px 80px 70px 110px 1fr 24px'
    : showStaffToggle
      ? '120px 80px 80px 110px minmax(88px,auto) 1fr 24px'
      : '120px 80px 80px 110px 1fr 24px';

  return (
    <div className="space-y-2">
      {horarios.map((slot) => (
        <div
          key={slot.tempId}
          className="grid gap-2 items-center p-2 bg-gray-50 rounded-lg overflow-x-auto"
          style={{ gridTemplateColumns: gridCols }}
        >
          <select
            value={slot.dia_semana}
            onChange={(e) => updateSlot(slot.tempId, { dia_semana: e.target.value })}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0"
          >
            {DIAS.map((d) => (
              <option key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </option>
            ))}
          </select>

          <input
            type="time"
            value={slot.hora_inicio?.slice(0, 5) || ''}
            onChange={(e) => updateSlot(slot.tempId, { hora_inicio: e.target.value })}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0"
          />

          <input
            type="time"
            value={slot.hora_fin?.slice(0, 5) || ''}
            onChange={(e) => updateSlot(slot.tempId, { hora_fin: e.target.value })}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0"
          />

          {showCapacity && (
            <div className="flex items-center gap-1.5 min-w-0">
              <input
                type="number"
                value={slot.capacidad_maxima ?? ''}
                onChange={(e) =>
                  updateSlot(slot.tempId, {
                    capacidad_maxima: e.target.value ? Number(e.target.value) : null,
                  })
                }
                placeholder="Cap."
                disabled={slot.capacidad_maxima === null}
                className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0 w-[62px] disabled:bg-gray-100 disabled:text-gray-400"
              />
              <label className="flex items-center gap-1 text-[11px] text-gray-600 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={slot.capacidad_maxima === null}
                  onChange={(e) =>
                    updateSlot(slot.tempId, {
                      capacidad_maxima: e.target.checked ? null : 10,
                    })
                  }
                  className="rounded border-gray-300"
                />
                Ilimitado
              </label>
            </div>
          )}

          <select
            value={slot.modalidad}
            onChange={(e) =>
              updateSlot(slot.tempId, {
                modalidad: e.target.value as 'presencial' | 'virtual' | 'hibrido',
              })
            }
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0"
          >
            <option value="presencial">Presencial</option>
            <option value="virtual">Virtual</option>
            <option value="hibrido">Híbrido</option>
          </select>

          {showStaffToggle && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
              <input
                type="checkbox"
                checked={slot.requiere_staff ?? false}
                onChange={(e) =>
                  updateSlot(slot.tempId, {
                    requiere_staff: e.target.checked,
                  })
                }
                className="rounded border-gray-300"
              />
              Staff
            </label>
          )}

          <input
            type="text"
            value={slot.ubicacion}
            onChange={(e) => updateSlot(slot.tempId, { ubicacion: e.target.value })}
            placeholder="Sala / URL"
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white min-w-0"
          />

          <button
            type="button"
            onClick={() => removeSlot(slot.tempId)}
            className="text-gray-400 hover:text-red-500 font-medium text-sm"
            aria-label="Quitar horario"
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addSlot}
        className="w-full text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg py-2 hover:bg-blue-50 transition-colors"
      >
        + Agregar horario
      </button>
    </div>
  );
}
